<script setup>
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { computed, onMounted, ref, watch } from 'vue';
import { useBranding } from 'shared/composables/useBranding';
import { picoSearch } from '@chatwoot/pico-search';
import IntegrationItem from './IntegrationItem.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePlatformPolicy } from 'dashboard/composables/usePlatformPolicy';
import {
  isManagedPlatformIntegration,
  isPlatformServiceEnabled,
} from 'dashboard/helper/platformPolicyRules';

const INTEGRATIONS_PER_PAGE = 12;

const store = useStore();
const getters = useStoreGetters();
const { replaceInstallationName } = useBranding();
const { accountId } = useAccount();
const { policy: platformPolicy, isPolicyLoading } =
  usePlatformPolicy(accountId);

const searchQuery = ref('');
const uiFlags = getters['integrations/getUIFlags'];
const currentPage = ref(1);

const integrationList = computed(() => {
  const integrationOrder = { foodics: 0, salla: 1, easy_orders: 2, daftara: 3 };
  const integrations = getters['integrations/getAppIntegrations'].value.filter(
    integration =>
      !isManagedPlatformIntegration(integration.id) ||
      isPlatformServiceEnabled(platformPolicy.value, integration.id)
  );

  return integrations.sort((a, b) => {
    const first = integrationOrder[a.id] ?? 100;
    const second = integrationOrder[b.id] ?? 100;
    return first - second;
  });
});

const filteredIntegrationList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return integrationList.value;
  return picoSearch(integrationList.value, query, ['name', 'description']);
});

const totalPages = computed(() =>
  Math.max(1, Math.ceil(filteredIntegrationList.value.length / INTEGRATIONS_PER_PAGE))
);

const paginatedIntegrationList = computed(() => {
  const start = (currentPage.value - 1) * INTEGRATIONS_PER_PAGE;
  return filteredIntegrationList.value.slice(start, start + INTEGRATIONS_PER_PAGE);
});

const setCurrentPage = page => {
  currentPage.value = Math.min(Math.max(page, 1), totalPages.value);
};

watch(searchQuery, () => {
  currentPage.value = 1;
});

watch(filteredIntegrationList, () => {
  if (currentPage.value > totalPages.value) currentPage.value = totalPages.value;
});

onMounted(() => {
  store.dispatch('integrations/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching || isPolicyLoading"
    :loading-message="$t('INTEGRATION_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INTEGRATION_SETTINGS.HEADER')"
        :description="
          replaceInstallationName($t('INTEGRATION_SETTINGS.DESCRIPTION'))
        "
        :link-text="$t('INTEGRATION_SETTINGS.LEARN_MORE')"
        :search-placeholder="$t('INTEGRATION_SETTINGS.SEARCH_PLACEHOLDER')"
        feature-name="integrations"
      />
    </template>
    <template #body>
      <div class="flex-grow flex-shrink overflow-auto">
        <span
          v-if="!filteredIntegrationList.length && searchQuery"
          class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
        >
          {{ $t('INTEGRATION_SETTINGS.NO_RESULTS') }}
        </span>
        <div
          v-else
          class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
        >
          <IntegrationItem
            v-for="item in paginatedIntegrationList"
            :id="item.id"
            :key="item.id"
            :logo="item.logo"
            :name="item.name"
            :description="item.description"
            :enabled="item.enabled"
          />
        </div>
        <div
          v-if="filteredIntegrationList.length > INTEGRATIONS_PER_PAGE"
          class="mt-5 flex items-center justify-between border-t border-n-weak pt-4"
        >
          <span class="text-sm text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.PAGE', { page: currentPage, total: totalPages }) }}
          </span>
          <div class="flex gap-2">
            <Button
              xs
              faded
              slate
              :disabled="currentPage <= 1"
              :label="$t('INTEGRATION_SETTINGS.PREVIOUS')"
              @click="setCurrentPage(currentPage - 1)"
            />
            <Button
              xs
              faded
              slate
              :disabled="currentPage >= totalPages"
              :label="$t('INTEGRATION_SETTINGS.NEXT')"
              @click="setCurrentPage(currentPage + 1)"
            />
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
